import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminFunctions.css";

/*
 * Import the configured Axios instance that installs
 * the rotating API-key interceptor.
 *
 * Adjust this path only if your file is elsewhere.
 */
import axios from "../../api/rotatingApiKey";

const AdminFunctions = ({ functions = [], selectedProject }) => {
  const navigate = useNavigate();

  const [countsByApi, setCountsByApi] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const visibleFunctions = useMemo(() => {
    const query = search.trim().toLowerCase();

    return functions
      .filter((item) => item.hidden !== true && item.hidden !== "true")
      .filter((item) =>
        String(item.name || "")
          .toLowerCase()
          .includes(query),
      );
  }, [functions, search]);

  const groupedFunctions = useMemo(() => {
    return visibleFunctions.reduce((groups, item) => {
      const category = item.category || "Other";

      if (!groups[category]) {
        groups[category] = [];
      }

      groups[category].push(item);

      return groups;
    }, {});
  }, [visibleFunctions]);

  const countApis = useMemo(() => {
    return [
      ...new Set(visibleFunctions.map((item) => item.countApi).filter(Boolean)),
    ];
  }, [visibleFunctions]);

  const getCategoryColor = (category = "Other") => {
    let hash = 0;

    for (let index = 0; index < category.length; index += 1) {
      hash = category.charCodeAt(index) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;

    return `hsl(${hue}, 78%, 58%)`;
  };

  /**
   * Add the selected project before the interceptor
   * appends the rotating key.
   *
   * Example input:
   * /uauc/count?status=Open
   *
   * Output before interceptor:
   * /uauc/count?status=Open&project=123
   *
   * Final request from interceptor:
   * /uauc/count?status=Open&project=123:KEY
   */
  const buildCountUrl = (api, projectId) => {
    const cleanApi = String(api || "").trim();

    if (!cleanApi) {
      return "";
    }

    if (!projectId) {
      return cleanApi;
    }

    try {
      const resolvedUrl = new URL(cleanApi, "https://rohanbackend.axiomos.in");

      /*
       * Avoid duplicate project parameters.
       */
      resolvedUrl.searchParams.set("project", projectId);

      if (/^https?:\/\//i.test(cleanApi)) {
        return resolvedUrl.toString();
      }

      return `${resolvedUrl.pathname}${resolvedUrl.search}`;
    } catch (error) {
      console.error("Unable to build count URL:", {
        api,
        projectId,
        error,
      });

      const separator = cleanApi.includes("?") ? "&" : "?";

      return (
        `${cleanApi}${separator}` + `project=${encodeURIComponent(projectId)}`
      );
    }
  };

  /**
   * Extract a usable count from different backend
   * response structures.
   */
  const extractCount = (payload) => {
    if (typeof payload === "number" && Number.isFinite(payload)) {
      return payload;
    }

    if (
      typeof payload === "string" &&
      payload.trim() !== "" &&
      Number.isFinite(Number(payload))
    ) {
      return Number(payload);
    }

    if (!payload) {
      return null;
    }

    const candidates = [
      payload?.count,
      payload?.total,
      payload?.data?.count,
      payload?.data?.total,
      payload?.result?.count,
      payload?.result?.total,
      payload?.data,
    ];

    const directCount = candidates.find(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== "" &&
        !Array.isArray(value) &&
        Number.isFinite(Number(value)),
    );

    if (directCount !== undefined) {
      return Number(directCount);
    }

    if (Array.isArray(payload)) {
      return payload.length;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data.length;
    }

    if (typeof payload === "object") {
      const firstNumericValue = Object.values(payload).find(
        (value) =>
          value !== null &&
          value !== "" &&
          !Array.isArray(value) &&
          Number.isFinite(Number(value)),
      );

      if (firstNumericValue !== undefined) {
        return Number(firstNumericValue);
      }
    }

    return null;
  };

  useEffect(() => {
    const controller = new AbortController();

    let isCurrentRequest = true;

    const fetchCounts = async () => {
      if (!countApis.length) {
        if (isCurrentRequest) {
          setCountsByApi({});
          setLoading(false);
        }

        return;
      }

      try {
        setLoading(true);

        const responses = await Promise.allSettled(
          countApis.map(async (api) => {
            const requestUrl = buildCountUrl(api, selectedProject);

            if (!requestUrl) {
              return {
                api,
                count: null,
              };
            }

            try {
              /*
               * This configured Axios instance applies
               * the rotating key automatically.
               *
               * Do not manually call fetchLatestApiKey
               * and do not append the key here.
               */
              const response = await axios.get(requestUrl, {
                signal: controller.signal,

                headers: {
                  Accept: "application/json",
                },
              });

              return {
                api,
                count: extractCount(response.data),
              };
            } catch (error) {
              if (
                error?.name === "CanceledError" ||
                error?.name === "AbortError" ||
                error?.code === "ERR_CANCELED"
              ) {
                throw error;
              }

              console.error("Count request failed:", {
                api,
                requestUrl,
                status: error?.response?.status,
                response: error?.response?.data,
                message: error?.message,
              });

              return {
                api,
                count: null,
              };
            }
          }),
        );

        if (!isCurrentRequest) {
          return;
        }

        const nextCounts = {};

        responses.forEach((result, index) => {
          const api = countApis[index];

          if (result.status === "fulfilled") {
            nextCounts[api] = result.value?.count ?? null;
          } else {
            nextCounts[api] = null;
          }
        });

        setCountsByApi(nextCounts);
      } catch (error) {
        if (
          error?.name !== "CanceledError" &&
          error?.name !== "AbortError" &&
          error?.code !== "ERR_CANCELED"
        ) {
          console.error("Unable to load module counts:", error);
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    fetchCounts();

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [countApis, selectedProject]);

const formatCount = (value) => {
  if (value === null || value === undefined) {
    return "—";
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return "—";
  }

  return numericValue.toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  });
};

  const openModule = (link) => {
    if (!link) return;

    navigate(link);
  };

  const handleModuleKeyDown = (event, link) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openModule(link);
    }
  };

  return (
    <div className="safety-admin-functions">
      <div className="safety-admin-functions__toolbar">
        <div className="safety-admin-functions__search">
          <i className="fas fa-search safety-admin-functions__search-icon" />

          <input
            type="text"
            placeholder="Search modules..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="safety-admin-functions__search-input"
          />
        </div>

        <div className="safety-admin-functions__meta">
          {visibleFunctions.length} module
          {visibleFunctions.length === 1 ? "" : "s"}
        </div>
      </div>

      {loading ? (
        <div className="safety-admin-functions__loading">
          <span className="safety-admin-functions__loader" />
          Loading modules...
        </div>
      ) : visibleFunctions.length === 0 ? (
        <div className="safety-admin-functions__empty">
          <i className="fas fa-layer-group safety-admin-functions__empty-icon" />

          <h3 className="safety-admin-functions__empty-title">
            No modules found
          </h3>

          <p className="safety-admin-functions__empty-text">
            Try another search keyword.
          </p>
        </div>
      ) : (
        Object.entries(groupedFunctions).map(([category, items]) => (
          <section
            key={category}
            className="safety-admin-functions__section"
            style={{
              "--category-accent": getCategoryColor(category),
            }}
          >
            <div className="safety-admin-functions__section-header">
              <h3 className="safety-admin-functions__section-title">
                {category}
              </h3>

              <span className="safety-admin-functions__section-count">
                {items.length}
              </span>
            </div>

            <div className="safety-admin-functions__grid">
              {items.map((item) => {
                const count = item.countApi ? countsByApi[item.countApi] : null;

                return (
                  <div
                    key={`${category}-${item.name}`}
                    className="safety-admin-functions__card"
                    onClick={() => openModule(item.link)}
                    onKeyDown={(event) => handleModuleKeyDown(event, item.link)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${item.name}`}
                  >
                    <div className="safety-admin-functions__card-content">
                      <div className="safety-admin-functions__card-main">
                        <span className="safety-admin-functions__card-title">
                          {item.name}
                        </span>

                        {item.countApi && (
                          <span className="safety-admin-functions__card-count">
                            {formatCount(count)}
                          </span>
                        )}
                      </div>

                      <div className="safety-admin-functions__icon-box">
                        {item.icon?.startsWith("fa-") ? (
                          <i
                            className={`fas ${item.icon} safety-admin-functions__icon`}
                          />
                        ) : (
                          <img
                            src={item.icon}
                            alt={item.name}
                            className="safety-admin-functions__icon safety-admin-functions__icon--image"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default AdminFunctions;
